import _ from 'lodash';
import momentTimezone from 'moment-timezone';

import { bookingUtils } from 'lib/booking';
import { globalVariables } from 'lib/globalVariables';

import type { ReactElement } from 'react';

const SessionStatusContainer = (props: any): ReactElement<any> | null => {
  // Shows an alert with the session status. It warns the client when:
  //  1) the session starts very soon, 2) someone is already booked in and confirmed,
  //  3) the client invited more people for the same session.
  // Not shown for focus groups, which always have more than one person.

  // Quant and longitudinal bookings don't have a specific session time assigned, so it doesn't make sense to render the status container
  if (bookingUtils.isOnlineTask(props.booking) || bookingUtils.isLongitudinal(props.booking)) return null;

  const { sessionStatus, participant } = props.singleSession;
  const minutesToStartSession = momentTimezone(_.get(props.session, 'start'))
    .tz(props.timezone)
    .diff(momentTimezone(), 'minutes');
  if (minutesToStartSession <= 60) {
    return (
      <div className="sessionStatusContainer warned">
        <p>
          This session is starting <strong>very soon</strong> ({minutesToStartSession} mins).
        </p>
        <p>
          {_.get(props, 'participant.user.meta.identity.firstname')} may not have enough notice to get there on time.
        </p>
      </div>
    );
  }
  if (props.previousAttendance) {
    return (
      <div className="sessionStatusContainer invited">
        <p>{_.get(participant, 'user.meta.identity.firstname')} has already been invited on another booking.</p>
        <div className="bookingContainer">
          <a
            href={`${globalVariables.getEnvironmentVariables().clientsAppURI}/bookings/${props.previousAttendance.booking._id}/participants/all`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {_.get(props, 'previousAttendance.booking.name')}
          </a>
          {(bookingUtils.isRemote(props.previousAttendance.booking) ||
            bookingUtils.isInPerson(props.previousAttendance.booking)) && (
            <p>
              {momentTimezone(_.get(props, 'previousAttendance.CurrentStatus.session.start'))
                .tz(props.previousAttendance.booking.config.timezone)
                .format('h:mm A')}{' '}
              -{' '}
              {momentTimezone(_.get(props, 'previousAttendance.CurrentStatus.session.end'))
                .tz(props.previousAttendance.booking.config.timezone)
                .format('h:mm A')}
            </p>
          )}
          <p>
            {momentTimezone(_.get(props, 'previousAttendance.CurrentStatus.session.start'))
              .tz(props.previousAttendance.booking.config.timezone)
              .format('dddd Do MMM')}
          </p>
        </div>
        <p>
          Your study exclusion rules don't allow participants to attend more than one opportunity for{' '}
          {props.booking.team.name}.
        </p>
      </div>
    );
  }
  if (!_.has(sessionStatus, 'status') || _.get(props.booking, 'config.session.type') === 2) return null;
  if (_.has(participant, 'invitedSession')) {
    return (
      <div className={`sessionStatusContainer ${sessionStatus.status}`}>
        <p>
          You sent {_.get(participant, 'user.meta.identity.firstname')} an invitation{' '}
          {momentTimezone(_.get(participant, 'invitedSession.history.session_invitation_notification')).fromNow()}.
        </p>
      </div>
    );
  }
  return (
    <div className={`sessionStatusContainer ${sessionStatus.status}`}>
      {sessionStatus.status === 'invited' ? (
        <p>
          You’ve already invited {sessionStatus.qtd === 1 ? '1 person' : `${sessionStatus.qtd} people`} to this session.
          The first person who responds gets the spot, all other invitees will get added to the waitlist.
        </p>
      ) : (
        <p>
          You’ve already confirmed a participant for this session. Any additional invitees will get added to the
          waitlist.
        </p>
      )}
    </div>
  );
};

export default SessionStatusContainer;
